import React, { useCallback, useMemo, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import { Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import { usePagination } from '@/shared/hooks/usePagination';
import { DEFAULT_PAGE_SIZE } from '@/shared/lib/constants';
import { EMPTY_MESSAGES } from '@/shared/lib/constants/messages';
import { highlightText } from '@/shared/lib/text';
import type { TagSource } from '@/shared/types/openmetadata';
import { AssetDescription } from '@/shared/ui/AssetDescription';
import { ColumnVisibilityButton, useColumnVisibility } from '@/shared/ui/ColumnVisibilityButton';
import { ErrorFallback } from '@/shared/ui/ErrorBoundary';
import { Icon } from '@/shared/ui/Icon';
import { SearchInput } from '@/shared/ui/SearchInput';
import { TableEmpty } from '@/shared/ui/TableEmpty';

import { filterEntityLabels } from '@/entities/search';
import { convertTagsToTree, useAvailableTags } from '@/entities/tags';

import type { TableConstraint } from '@/features/asset-detail/api/baseApi';
import type { ColumnSchema } from '@/features/asset-detail/api/schemaApi';
import { useGlossaryTree, useUpdateColumnTags } from '@/features/asset-detail/model';
import { GlossaryTermsCell, TagsCell } from '@/features/asset-detail/ui';

import { SEARCH_PLACEHOLDERS } from '@/widgets/asset-detail/shared/constants';

import styles from './SchemaTab.module.css';

const { Text } = Typography;

type SchemaTabProps = {
  columns: ColumnSchema[];
  tableConstraints?: TableConstraint[];
  tableFqn: string;
  entityType: string;
  isLoading?: boolean;
  isError?: boolean;
  error?: Error | null;
  onRetry?: () => void;
};

const COLUMN_WIDTHS = {
  NAME: 200,
  DATA_TYPE: 200,
  DESCRIPTION: 352,
  TAGS: 352,
  GLOSSARY_TERMS: 352,
} as const;

type ColumnKey = 'name' | 'dataType' | 'description' | 'tags' | 'glossaryTerms';

const SchemaTabComponent: React.FC<SchemaTabProps> = ({
  columns,
  tableConstraints,
  tableFqn,
  entityType,
  isLoading = false,
  isError = false,
  error,
  onRetry,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState<string>('');

  const { data: tagsData, isLoading: _isLoadingTags } = useAvailableTags();
  const { data: glossaryTree, isLoading: _isLoadingGlossary } = useGlossaryTree();
  const { mutateAsync: updateColumnTags } = useUpdateColumnTags(entityType, tableFqn);
  const [updatingColumns, setUpdatingColumns] = useState<Set<string>>(new Set());

  const tagsTreeData = useMemo(() => {
    if (!tagsData || tagsData.length === 0) return [];
    return convertTagsToTree(tagsData);
  }, [tagsData]);

  const handleUpdateColumnTags = useCallback(
    async (params: { tableFqn: string; columnName: string; tagFQNs: string[]; source: TagSource }) => {
      const key = `${params.columnName}-${params.source}`;
      setUpdatingColumns((prev) => new Set(prev).add(key));
      try {
        await updateColumnTags(params);
      } finally {
        setUpdatingColumns((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [updateColumnTags]
  );

  const DEFAULT_COLUMN_ORDER = useMemo<ColumnKey[]>(() => {
    return ['name', 'dataType', 'description', 'tags', 'glossaryTerms'];
  }, []);

  const DEFAULT_VISIBLE_COLUMNS = useMemo<ColumnKey[]>(() => {
    return DEFAULT_COLUMN_ORDER;
  }, [DEFAULT_COLUMN_ORDER]);

  const hasData = columns && columns.length > 0;

  const constraintMap = useMemo(() => {
    const map: Record<string, { isPrimaryKey: boolean; isForeignKey: boolean }> = {};

    if (tableConstraints) {
      tableConstraints.forEach((constraint) => {
        constraint.columns.forEach((columnName) => {
          if (!map[columnName]) {
            map[columnName] = { isPrimaryKey: false, isForeignKey: false };
          }
          if (constraint.constraintType === 'PRIMARY_KEY') {
            map[columnName].isPrimaryKey = true;
          } else if (constraint.constraintType === 'FOREIGN_KEY') {
            map[columnName].isForeignKey = true;
          }
        });
      });
    }

    return map;
  }, [tableConstraints]);

  const getConstraintIcons = useCallback(
    (column: ColumnSchema) => {
      const constraints = constraintMap[column.name];
      const hasConstraints = constraints || column.constraint === 'NOT_NULL';

      if (!hasConstraints) return null;

      return (
        <>
          {constraints?.isPrimaryKey && (
            <Icon
              name="primary-key"
              showTooltip
              tooltipTitle="Primary Key"
              className={`${styles.constraintIcon} ${styles.primaryKeyIcon}`}
            />
          )}
          {constraints?.isForeignKey && (
            <Icon
              name="foreign-key"
              showTooltip
              tooltipTitle="Foreign Key"
              className={`${styles.constraintIcon} ${styles.foreignKeyIcon}`}
            />
          )}
          {column.constraint === 'NOT_NULL' && (
            <Icon
              name="not-null"
              showTooltip
              tooltipTitle="Not Null"
              className={`${styles.constraintIcon} ${styles.notNullIcon}`}
            />
          )}
        </>
      );
    },
    [constraintMap]
  );

  const filteredColumns = useMemo(() => {
    if (!searchQuery || searchQuery.length === 0) return columns;

    const lowerQuery = searchQuery.toLowerCase();
    return columns.filter((column) => {
      const nameMatch = column.name.toLowerCase().includes(lowerQuery);
      const descriptionMatch = column.description?.toLowerCase().includes(lowerQuery);

      const classificationTags = filterEntityLabels(column.tags, {
        includeOnlySource: 'Classification',
        requireConfirmed: true,
      });
      const glossaryTerms = filterEntityLabels(column.tags, {
        includeOnlySource: 'Glossary',
        requireConfirmed: true,
      });

      const tagMatch = classificationTags?.some((tag) => tag.tagFQN?.toLowerCase().includes(lowerQuery));
      const termMatch = glossaryTerms?.some((term) => term.tagFQN?.toLowerCase().includes(lowerQuery));

      return nameMatch || descriptionMatch || tagMatch || termMatch;
    });
  }, [columns, searchQuery]);

  const allColumnsMap: Record<ColumnKey, ColumnsType<ColumnSchema>[number]> = useMemo(() => {
    const cols: Record<string, ColumnsType<ColumnSchema>[number]> = {
      name: {
        title: '컬럼명',
        dataIndex: 'name',
        key: 'name',
        width: COLUMN_WIDTHS.NAME,
        fixed: 'left',
        render: (_name: string, record: ColumnSchema) => (
          <div className={styles.columnNameWrapper}>
            <Text className={styles.columnName}>
              {highlightText(record.name, searchQuery, { className: 'highlight' })}
            </Text>
            {getConstraintIcons(record)}
          </div>
        ),
        sorter: (a, b) => a.name.localeCompare(b.name),
      },
      dataType: {
        title: '데이터 타입',
        dataIndex: 'dataType',
        key: 'dataType',
        width: COLUMN_WIDTHS.DATA_TYPE,
        render: (dataType: string, record: ColumnSchema) => (
          <Text className={styles.dataType}>
            {highlightText(dataType || record.dataTypeDisplay || '', searchQuery, { className: 'highlight' })}
          </Text>
        ),
      },
      description: {
        title: '설명',
        dataIndex: 'description',
        key: 'description',
        width: hasData ? COLUMN_WIDTHS.DESCRIPTION : 'auto',
        render: (description?: string) => {
          return <AssetDescription description={description} mode="expandable" highlightQuery={searchQuery.trim()} />;
        },
      },
      tags: {
        title: '태그',
        dataIndex: 'tags',
        key: 'tags',
        width: COLUMN_WIDTHS.TAGS,
        render: (tags: ColumnSchema['tags'], record: ColumnSchema) => (
          <TagsCell
            tags={tags}
            columnName={record.name}
            tableFqn={tableFqn}
            tagsTreeData={tagsTreeData}
            updateColumnTags={handleUpdateColumnTags}
            isUpdating={updatingColumns.has(`${record.name}-Classification`)}
          />
        ),
      },
      glossaryTerms: {
        title: '용어',
        dataIndex: 'tags',
        key: 'glossaryTerms',
        width: COLUMN_WIDTHS.GLOSSARY_TERMS,
        render: (tags: ColumnSchema['tags'], record: ColumnSchema) => (
          <GlossaryTermsCell
            tags={tags}
            columnName={record.name}
            tableFqn={tableFqn}
            glossaryTree={glossaryTree || []}
            updateColumnTags={handleUpdateColumnTags}
            isUpdating={updatingColumns.has(`${record.name}-Glossary`)}
          />
        ),
      },
    };

    return cols as Record<ColumnKey, ColumnsType<ColumnSchema>[number]>;
  }, [
    getConstraintIcons,
    hasData,
    tableFqn,
    tagsTreeData,
    glossaryTree,
    handleUpdateColumnTags,
    updatingColumns,
    searchQuery,
  ]);

  const allColumnsMapForVisibility = useMemo<Record<ColumnKey, { title: string }>>(() => {
    return Object.keys(allColumnsMap).reduce(
      (acc, key) => {
        acc[key as ColumnKey] = { title: allColumnsMap[key as ColumnKey].title as string };
        return acc;
      },
      {} as Record<ColumnKey, { title: string }>
    );
  }, [allColumnsMap]);

  const { state } = useColumnVisibility<ColumnKey>({
    defaultColumnOrder: DEFAULT_COLUMN_ORDER,
    defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
    allColumnsMap: allColumnsMapForVisibility,
    columnOrderParamKey: 'columnOrder',
    visibleColumnsParamKey: 'columns',
  });

  const columnOrder = state.columnOrder;
  const visibleColumns = state.visibleColumns;

  const visibleColumnsData = useMemo<ColumnsType<ColumnSchema>>(() => {
    return columnOrder
      .filter((key) => Object.hasOwn(allColumnsMap, key))
      .filter((key) => visibleColumns.includes(key))
      .map((key) => allColumnsMap[key]);
  }, [columnOrder, visibleColumns, allColumnsMap]);

  const pageParam = searchParams.get('page');
  const pageSizeParam = searchParams.get('pageSize');
  const initialPage = pageParam ? parseInt(pageParam, 10) : 1;
  const initialPageSize = pageSizeParam ? parseInt(pageSizeParam, 10) : DEFAULT_PAGE_SIZE;

  const handlePageChange = useCallback(
    (page: number, pageSize: number) => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('page', String(page));
      newSearchParams.set('pageSize', String(pageSize));
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const flatColumns = useMemo(() => {
    if (!filteredColumns || filteredColumns.length === 0) return [];
    return filteredColumns.map(({ children: _children, ...rest }) => rest);
  }, [filteredColumns]);

  const { paginationConfig, scrollConfig } = usePagination({
    dataLength: flatColumns.length,
    viewportOffset: 648,
    initialPage,
    initialPageSize,
    onPageChange: handlePageChange,
  });

  if (isError) {
    return (
      <ErrorFallback
        error={error}
        description="컬럼 정보를 불러오지 못했습니다. 다시 시도해 주세요."
        resetErrorBoundary={onRetry}
      />
    );
  }

  return (
    <div className={styles.schemaTab}>
      <div className={styles.toolbar}>
        <SearchInput
          placeholder={SEARCH_PLACEHOLDERS.SCHEMA}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div className={styles.toolbarRight}>
          <ColumnVisibilityButton<ColumnKey>
            defaultColumnOrder={DEFAULT_COLUMN_ORDER}
            defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
            allColumnsMap={allColumnsMapForVisibility}
            columnOrderParamKey="columnOrder"
            visibleColumnsParamKey="columns"
          />
        </div>
      </div>
      <Table
        columns={visibleColumnsData}
        dataSource={flatColumns}
        rowKey="name"
        loading={isLoading}
        pagination={paginationConfig}
        scroll={{ x: flatColumns.length > 0 ? 'max-content' : undefined, ...scrollConfig }}
        locale={{
          emptyText: (
            <TableEmpty
              searchQuery={searchQuery}
              description={searchQuery ? undefined : EMPTY_MESSAGES.NO_TABLE_ITEMS}
            />
          ),
        }}
        className={styles.schemaTable}
      />
    </div>
  );
};

export const SchemaTab = React.memo(SchemaTabComponent);
